import { useState, useCallback, useEffect } from 'react';
import axios from '../api/axios';
import Cookies from 'js-cookie';

export const useCrudOperations = (config) => {
  const {
    baseUrl,
    endpoints,
    validateForm,
    initialFormState,
    entityName = 'item',
    fetchMethod = 'GET',
    deleteMethod = 'GET',
    fetchPayload = {},
    deletePayload = {},
    customFetch
  } = config;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState(initialFormState);
  const [formErrors, setFormErrors] = useState({});
  const [editId, setEditId] = useState(null);
 useEffect(() => {
    console.log("--- FORM STATE UPDATED ---");
    console.log(form);
  }, [form]);

  const token = Cookies.get('token');
  const userId = Cookies.get('userId') || 1;

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let itemsData = [];
      
      if (customFetch) {
        itemsData = await customFetch(`${baseUrl}${endpoints.fetch}`);
      } else if (fetchMethod === 'POST') {
        const res = await axios.post(`${baseUrl}${endpoints.fetch}`, fetchPayload, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        const dataKey = Object.keys(res.data).find(key => Array.isArray(res.data[key]));
        if (dataKey) {
          itemsData = res.data[dataKey];
        } else if (Array.isArray(res.data)) {
          itemsData = res.data;
        }
      } else {
        const res = await axios.get(`${baseUrl}${endpoints.fetch}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const dataKey = Object.keys(res.data).find(key => Array.isArray(res.data[key]));
        if (dataKey) {
          itemsData = res.data[dataKey];
        } else if (Array.isArray(res.data)) {
          itemsData = res.data;
        }
      }
      
      const transformedItems = itemsData.map(item => ({
        ...item,
        exIsActive: item.isactive !== undefined ? item.isactive : item.exIsActive,
        originalIsActive: item.isactive
      }));
      
      setItems(transformedItems);
    } catch (err) {
      setError(`Failed to fetch ${entityName}s. Please try again.`);
    } finally {
      setLoading(false);
    }
  }, [token, baseUrl, endpoints.fetch, entityName, fetchMethod, customFetch, JSON.stringify(fetchPayload)]);

  const resetForm = useCallback(() => {
    setForm(initialFormState);
    setFormErrors({});
    setEditId(null);
  }, [initialFormState]);

  // ✅ THIS IS THE CORRECTED, UNIVERSAL handleChange FUNCTION
  const handleChange = (e) => {
    // This function can now handle standard inputs, switches, and custom components.
    let name, value;

    if (e.target) {
      // This handles standard HTML/React-Bootstrap inputs (text, select, textarea, etc.)
      name = e.target.name;
      const type = e.target.type;
      // For switches or checkboxes, the new value is in `e.target.checked`, not `e.target.value`
      value = (type === 'checkbox' || type === 'switch') ? e.target.checked : e.target.value;
    } else {
      // This handles custom components (like a MultiSelect) that might pass an object
      // directly, like { name: 'colleges', value: ['1', '2'] }
      name = e.name;
      value = e.value;
    }

    // Update the form state with the new value
    setForm(prev => ({ ...prev, [name]: value }));
    
    // Clear any existing validation error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e, transformPayload) => {
    e.preventDefault();
    const errors = validateForm(form);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return false;
    }

    setSubmitting(true);
    setError('');
    try {
      const payload = transformPayload ? transformPayload(form, editId, userId) : {
        ...form,
        user: userId,
        ...(editId && { id: editId })
      };
      
      const endpoint = editId ? endpoints.update : endpoints.save;
      const res = await axios.post(`${baseUrl}${endpoint}`, payload, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      
      setSuccess(res.data.message || `${entityName} ${editId ? 'updated' : 'added'} successfully!`);
      resetForm();
      fetchItems();
      setTimeout(() => setSuccess(''), 4000);
      return true;
    } catch (error) {
      setError(`Failed to ${editId ? 'update' : 'save'} ${entityName.toLowerCase()}.`);
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (item) => {
    setSubmitting(true);
    try {
      if (deleteMethod === 'POST') {
        const payload = {
          id: item.id,
          ...deletePayload
        };
        await axios.post(`${baseUrl}${endpoints.delete}`, payload, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      } else {
        await axios.get(`${baseUrl}${endpoints.delete}?id=${item.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      setSuccess(`${entityName} deleted successfully!`);
      fetchItems();
      setTimeout(() => setSuccess(''), 4000);
      return true;
    } catch (error) {
      setError(`Failed to delete ${entityName.toLowerCase()}.`);
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (item, transformToForm) => {
    setEditId(item.id);
    setForm(transformToForm ? transformToForm(item) : item);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleStatus = async (item, statusField = 'exIsActive') => {
    const updatedStatus = item[statusField] ? 0 : 1;
    
    setItems(currentItems =>
      currentItems.map(i => i.id === item.id ? { 
        ...i, 
        [statusField]: updatedStatus,
        ...(item.originalIsActive !== undefined && { isactive: !!updatedStatus })
      } : i)
    );

    try {
      const payloadStatusField = item.originalIsActive !== undefined ? 'isactive' : statusField;
      
      await axios.post(`${baseUrl}${endpoints.update}`, {
        id: item.id,
        [payloadStatusField]: !!updatedStatus,
        user: userId
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      setSuccess(`${entityName} status updated successfully!`);
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError('Failed to update status.');
      setItems(currentItems =>
        currentItems.map(i => i.id === item.id ? { 
          ...i, 
          [statusField]: item[statusField],
          ...(item.originalIsActive !== undefined && { isactive: item.originalIsActive })
        } : i)
      );
    }
  };

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  return {
    items,
    loading,
    submitting,
    error,
    success,
    form,
    formErrors,
    editId,
    setError,
    setSuccess,
    fetchItems,
    resetForm,
    handleChange,
    handleSubmit,
    handleDelete,
    handleEdit,
    toggleStatus
  };
};